import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowDownTrayIcon, CheckIcon } from "@heroicons/react/24/outline";
import { RiShare2Fill } from "react-icons/ri";
import QRCode from "qrcode";
import { showToast } from "../utils/toast";

/** White modules, transparent “quiet” area — shows modal purple behind. */
const QR_OPTIONS = {
  width: 320,
  margin: 2,
  color: {
    dark: "#FFFFFF",
    light: "#00000000",
  },
};

/**
 * High-res “payment app style” share image: purple frame, white rounded card,
 * black-on-white QR (best for scanners), headline + helper copy.
 */
async function buildShareCardPngDataUrl(shareUrl) {
  const W = 1200;
  const H = 1500;
  const cardX = 56;
  const cardY = 72;
  const cardW = W - cardX * 2;
  const cardH = H - cardY - 140;
  const innerPad = 52;
  const titleBlock = 76;
  const footerBlock = 100;
  const maxQr = Math.min(
    880,
    cardW - innerPad * 2,
    cardH - titleBlock - footerBlock - innerPad * 2,
  );
  const qrSize = Math.floor(maxQr / 4) * 4;

  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, shareUrl, {
    width: qrSize,
    margin: 4,
    errorCorrectionLevel: "H",
    color: { dark: "#000000", light: "#FFFFFF" },
  });

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "#12051f");
  grad.addColorStop(0.45, "#230046");
  grad.addColorStop(1, "#5701AE");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  const rCard = 40;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 16;
  fillRoundedRect(ctx, cardX, cardY, cardW, cardH, rCard, "#ffffff");
  ctx.restore();

  const cx = cardX + cardW / 2;
  let y = cardY + innerPad + 8;

  ctx.fillStyle = "#0f172a";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.font =
    '600 44px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.fillText("Scan to view our events", cx, y);
  y += titleBlock;

  const qrDrawX = Math.round(cx - qrSize / 2);
  const qrDrawY = Math.round(y);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(qrDrawX - 20, qrDrawY - 20, qrSize + 40, qrSize + 40);
  ctx.strokeStyle = "rgba(15, 23, 42, 0.08)";
  ctx.lineWidth = 2;
  ctx.strokeRect(
    qrDrawX - 20 + 1,
    qrDrawY - 20 + 1,
    qrSize + 40 - 2,
    qrSize + 40 - 2,
  );
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(
    qrCanvas,
    0,
    0,
    qrSize,
    qrSize,
    qrDrawX,
    qrDrawY,
    qrSize,
    qrSize,
  );

  y = qrDrawY + qrSize + 36;
  ctx.fillStyle = "#64748b";
  ctx.font =
    '28px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.fillText("Open your camera and point it at the code", cx, y);
  y += 40;

  ctx.fillStyle = "#94a3b8";
  ctx.font = "22px ui-monospace, Menlo, Consolas, monospace";
  const maxChars = 52;
  const line =
    shareUrl.length > maxChars
      ? `${shareUrl.slice(0, maxChars - 1)}…`
      : shareUrl;
  ctx.fillText(line, cx, y);

  const stripY = H - 88;
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ctx.fillRect(0, stripY, W, H - stripY);
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font =
    '26px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.fillText("Seymour · Organization events", cx, stripY + 30);

  return canvas.toDataURL("image/png", 1.0);
}

function fillRoundedRect(ctx, x, y, width, height, radius, fillStyle) {
  const rad = Math.min(radius, width / 2, height / 2);
  ctx.fillStyle = fillStyle;
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, rad);
  } else {
    ctx.moveTo(x + rad, y);
    ctx.lineTo(x + width - rad, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + rad);
    ctx.lineTo(x + width, y + height - rad);
    ctx.quadraticCurveTo(x + width, y + height, x + width - rad, y + height);
    ctx.lineTo(x + rad, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - rad);
    ctx.lineTo(x, y + rad);
    ctx.quadraticCurveTo(x, y, x + rad, y);
  }
  ctx.closePath();
  ctx.fill();
}

export default function ShareEventsModal({ open, onClose, shareUrl }) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setQrDownloaded(false);
      setCopied(false);
      setQrDataUrl("");
      return;
    }
    if (!shareUrl) return;

    let cancelled = false;
    QRCode.toDataURL(shareUrl, QR_OPTIONS)
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl("");
      });

    return () => {
      cancelled = true;
    };
  }, [open, shareUrl]);

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showToast("Link copied to clipboard!", { type: "success" });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("Copy failed", { type: "error" });
    }
  };

  const downloadQrPng = async () => {
    if (!shareUrl) return;
    try {
      const dataUrl = await buildShareCardPngDataUrl(shareUrl);
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "seymour-events-qr.png";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setQrDownloaded(true);
      showToast("QR code downloaded", { type: "success" });
    } catch {
      showToast("Download failed", { type: "error" });
    }
  };

  const shareLink = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Our events",
          text: "View our upcoming events",
          url: shareUrl,
        });
      } catch (err) {
        if (err?.name !== "AbortError") {
          showToast("Could not open share sheet", { type: "error" });
        }
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      showToast("Link copied — paste it to share", { type: "success" });
    } catch {
      showToast("Copy failed", { type: "error" });
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto overflow-x-hidden">
          <div
            className="min-h-full flex items-end justify-center p-0 sm:items-center sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200 motion-reduce:transition-none"
              enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150 motion-reduce:transition-none"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-1 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-none sm:max-w-md rounded-t-[20px] border border-b-0 border-[#6A6680] sm:rounded-[20px] sm:border-b bg-[#230046] shadow-2xl max-h-[90dvh] sm:max-h-none overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] sm:pb-0">
                <div className="p-5 sm:p-6 relative">
                  <button
                    type="button"
                    className="shrink-0 rounded-lg p-1 text-white !bg-transparent absolute right-2 top-2 z-10"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-7 w-7" />
                  </button>
                  <div className="flex items-start justify-center gap-3">
                    <Dialog.Title className="text-xl sm:text-[22px] font-bold text-white atkinson-font text-center">
                      Share Organization Link
                    </Dialog.Title>
                  </div>

                  <p className="mt-2 text-sm text-white/80 atkinson-font leading-relaxed text-center">
                    Have attendees scan the QR code,
                    <br />
                    or share the link to view your events.
                  </p>

                  <div className="mt-5 flex justify-center rounded-xl">
                    {qrDataUrl ? (
                      <img
                        src={qrDataUrl}
                        alt="QR code linking to your public events page"
                        width={320}
                        height={320}
                        className="h-auto w-full max-w-[min(100%,320px)]"
                      />
                    ) : (
                      <div className="flex aspect-square w-full max-w-[min(100%,320px)] items-center justify-center text-sm text-white/50 atkinson-font">
                        {shareUrl ? "Generating…" : "No link available"}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    style={{ border: "2px solid #6A6680" }}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-transparent py-3 px-4 text-sm font-semibold text-white hover:bg-white/5 transition-colors atkinson-font"
                    onClick={downloadQrPng}
                    disabled={!shareUrl}
                  >
                    {qrDownloaded ? (
                      <>
                        <CheckIcon className="h-5 w-5 text-emerald-400" />
                        Downloaded QR code
                      </>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-5 w-5" />
                        Download QR Code as PNG
                      </>
                    )}
                  </button>

                  <div
                    className="mt-6 flex items-center gap-3"
                    role="separator"
                    aria-label="Share via link"
                  >
                    <div className="h-[2px] flex-1 bg-white" />
                    <span className="shrink-0 text-sm font-semibold tracking-wide text-white atkinson-font">
                      Share via link
                    </span>
                    <div className="h-[2px] flex-1 bg-white" />
                  </div>

                  <div className="flex flex-col gap-3 relative justify-center mt-4">
                    <input
                      readOnly
                      value={
                        shareUrl?.length < 35
                          ? shareUrl
                          : shareUrl?.slice(0, 32) + "..." || ""
                      }
                      className="min-w-0 flex-1 bg-transparent py-3 pl-3 pr-2 outline-none atkinson-font sm:text-sm text-ellipsis atkinson-fontbg-transparent border border-[#FFFFFF] rounded-lg p-3 text-white text-sm break-all"
                      aria-label="Public events link"
                    />
                    <button
                      type="button"
                      style={{
                        border: "2px solid #6A6680",
                      }}
                      className="absolute w-fit right-0 !bg-transparent text-white text-[13px] rounded-lg py-1 px-3 font-semibold shadow-[0_0_0_1px_rgba(255,255,255,0.2)] transition-colors"
                      onClick={copyLink}
                      disabled={!shareUrl}
                    >
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>

                  <button
                    type="button"
                    style={{ border: "2px solid #6A6680" }}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[#5701AE] py-3 px-4 text-sm font-semibold text-white hover:bg-[#7c4dff] transition-colors atkinson-font"
                    onClick={shareLink}
                    disabled={!shareUrl}
                  >
                    <RiShare2Fill className="h-5 w-5" />
                    Send Link to...
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
